/**
 * Case Membership System - Role-Based Permissions for Pro/SRL Collaboration
 * 
 * Extends the existing cases/{caseId}/members/{uid} subcollection with:
 * - Granular permission flags
 * - Pro-specific role types
 * - Permission preset helpers
 * 
 * @see WORK_ORDER_PRO_SRL_SHARED_WORKSPACE.md
 */

import { Timestamp } from 'firebase/firestore';

// ===== CANONICAL ROLE CONSTANTS =====

/**
 * Single canonical source for all case member role strings.
 * Use these constants instead of hardcoding role strings throughout the app.
 * LEGACY_MEMBER covers historical flat-array membership (no subcollection doc).
 */
export const CASE_MEMBER_ROLES = {
  SRL_OWNER:     'SRL_OWNER',
  PRO_PRIMARY:   'PRO_PRIMARY',
  PRO_COLLAB:    'PRO_COLLAB',
  SRL_COLLAB:    'SRL_COLLAB',
  VIEW_ONLY:     'VIEW_ONLY',
  LEGACY_MEMBER: 'member',
} as const;

/**
 * Normalizes any known role string (including legacy values) to the canonical
 * role string from CASE_MEMBER_ROLES. Returns null for unknown or empty input.
 * Apply this at all read boundaries before comparing role values.
 */
export function normalizeRole(raw: string | null | undefined): string | null {
  if (!raw) return null;
  switch (raw.toLowerCase()) {
    case 'srl_owner':
    case 'owner':
      return CASE_MEMBER_ROLES.SRL_OWNER;
    case 'pro_primary':
      return CASE_MEMBER_ROLES.PRO_PRIMARY;
    case 'pro_collab':
      return CASE_MEMBER_ROLES.PRO_COLLAB;
    case 'srl_collab':
      return CASE_MEMBER_ROLES.SRL_COLLAB;
    case 'view_only':
      return CASE_MEMBER_ROLES.VIEW_ONLY;
    case 'member':
    case 'collaborator':
    case 'editor':
      return CASE_MEMBER_ROLES.SRL_COLLAB;
    default:
      console.warn(`[normalizeRole] Unknown role string: "${raw}"`);
      return null;
  }
}

// ===== ROLE TYPES =====

/**
 * Case member role types.
 * 
 * - SRL_OWNER: Case creator, full access (auto-assigned to case.ownerUid)
 * - PRO_PRIMARY: Primary professional (attorney), full collaboration access
 * - PRO_COLLAB: Collaborating professional, full access but cannot manage members
 * - SRL_COLLAB: SRL collaborator (friend/family helping), limited access
 * - VIEW_ONLY: Read-only access, no write permissions
 */
export type CaseMemberRole = 
  | 'SRL_OWNER'
  | 'PRO_PRIMARY'
  | 'PRO_COLLAB'
  | 'SRL_COLLAB'
  | 'VIEW_ONLY';

// ===== PERMISSION FLAGS =====

/**
 * Granular permission flags for case workspace access.
 * 
 * All members have canRead=true. Write permissions are role-specific.
 * Pro users have canDelete defaulted to false for safety.
 */
export interface CaseMemberPermissions {
  canRead: boolean;              // Always true for active members
  canWriteJournal: boolean;      // Add/edit journal entries
  canWriteForms: boolean;        // Create/edit form instances
  canWriteFilings: boolean;      // Prepare/edit filings
  canWriteExhibits: boolean;     // Upload/tag exhibits
  canManageChecklists: boolean;  // Assign/complete checklist items
  canInvite: boolean;            // Invite additional members
  canRemoveMembers: boolean;     // Remove other members (owner only)
  canDelete: boolean;            // Delete case content (owner only by default)
}

// ===== MEMBERSHIP DOCUMENT =====

/**
 * Case membership document stored at cases/{caseId}/members/{uid}.
 * 
 * Created when:
 * - SRL creates case (owner membership auto-created)
 * - Connection/invitation accepted (Pro or SRL collaborator added)
 * 
 * Status lifecycle:
 * - pending: Invitation sent, not yet accepted
 * - active: Member has access to case workspace
 * - revoked: Access removed by owner
 */
export interface CaseMember {
  uid: string;                          // User ID
  role: CaseMemberRole;                 // Role determines default permissions
  permissions: CaseMemberPermissions;   // Granular permission flags
  status: 'active' | 'revoked' | 'pending';
  joinedAt: Timestamp;                  // When user joined (accepted invitation)
  invitedBy: string;                    // UID of user who invited
  invitedAt?: Timestamp;                // When invitation was sent (if applicable)
  revokedAt?: Timestamp;                // When access was revoked (if applicable)
  revokedBy?: string;                   // UID of user who revoked access (if applicable)
}

// ===== PRO CLIENT REFERENCE =====

/**
 * Pro client reference stored at proUsers/{proUid}/clients/{clientRefId}.
 * 
 * Created when Pro accepts case invitation. Provides explicit navigation
 * from Pro dashboard to case workspace without relying on "currentCaseId" resolution.
 */
export interface ProClient {
  caseId: string;                       // Reference to cases/{caseId}
  clientUid: string;                    // SRL user ID (case owner)
  clientDisplayName: string;            // SRL display name
  caseTitle: string;                    // Case title for display
  status: 'active' | 'archived';        // Active = current client, archived = past client
  lastActivityAt: Timestamp;            // Last time Pro interacted with case
  addedAt: Timestamp;                   // When Pro was added to case
}

// ===== PERMISSION PRESETS =====

/**
 * Get default permissions for a given role.
 * 
 * These are presets - individual permissions can be customized if needed,
 * but for Phase 1 we use role presets only.
 * 
 * @param role Case member role
 * @returns Default permissions for that role
 */
export function getDefaultPermissions(role: CaseMemberRole): CaseMemberPermissions {
  switch (role) {
    case 'SRL_OWNER':
      return {
        canRead: true,
        canWriteJournal: true,
        canWriteForms: true,
        canWriteFilings: true,
        canWriteExhibits: true,
        canManageChecklists: true,
        canInvite: true,
        canRemoveMembers: true,
        canDelete: true,
      };
    
    case 'PRO_PRIMARY':
      return {
        canRead: true,
        canWriteJournal: true,
        canWriteForms: true,
        canWriteFilings: true,
        canWriteExhibits: true,
        canManageChecklists: true,
        canInvite: true,           // Can invite other pros
        canRemoveMembers: false,   // Cannot remove SRL owner
        canDelete: false,          // Cannot delete case content (safety)
      };
    
    case 'PRO_COLLAB':
      return {
        canRead: true,
        canWriteJournal: true,
        canWriteForms: true,
        canWriteFilings: true,
        canWriteExhibits: true,
        canManageChecklists: true,
        canInvite: false,          // Cannot invite others
        canRemoveMembers: false,
        canDelete: false,
      };
    
    case 'SRL_COLLAB':
      return {
        canRead: true,
        canWriteJournal: true,     // Can add journal entries
        canWriteForms: false,      // No forms access
        canWriteFilings: false,    // No filings access
        canWriteExhibits: true,    // Can upload exhibits
        canManageChecklists: true, // Can help with tasks
        canInvite: false,
        canRemoveMembers: false,
        canDelete: false,
      };
    
    case 'VIEW_ONLY':
      return {
        canRead: true,
        canWriteJournal: false,
        canWriteForms: false,
        canWriteFilings: false,
        canWriteExhibits: false,
        canManageChecklists: false,
        canInvite: false,
        canRemoveMembers: false,
        canDelete: false,
      };
  }
}

/**
 * Get human-readable label for a role.
 * 
 * @param role Case member role
 * @returns Display label
 */
export function getRoleLabel(role: CaseMemberRole): string {
  switch (role) {
    case 'SRL_OWNER': return 'Owner';
    case 'PRO_PRIMARY': return 'Primary Counsel';
    case 'PRO_COLLAB': return 'Collaborating Counsel';
    case 'SRL_COLLAB': return 'Collaborator';
    case 'VIEW_ONLY': return 'Viewer';
  }
}

/**
 * Check if a role is a Professional role.
 * 
 * @param role Case member role
 * @returns True if role is PRO_PRIMARY or PRO_COLLAB
 */
export function isProRole(role: CaseMemberRole): boolean {
  return role === 'PRO_PRIMARY' || role === 'PRO_COLLAB';
}

/**
 * Check if a role is an SRL role.
 * 
 * @param role Case member role
 * @returns True if role is SRL_OWNER or SRL_COLLAB
 */
export function isSRLRole(role: CaseMemberRole): boolean {
  return role === 'SRL_OWNER' || role === 'SRL_COLLAB';
}
